import { useTranslation } from 'react-i18next'
import {
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  Clock,
  Copy,
  FileText,
  Mail,
  Pencil,
  User,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

import { useLessonSheet } from '../LessonSheetContext'
import { JOURNALS, LESSON_SLOT_LABELS } from '../../data/subject-teacher-mock'

const MOCK_HOMEWORK: Record<number, { title: string; description: string; deadline: string }> = {
  1: {
    title: 'Worksheet p. 42 — pair interviews',
    description: 'Complete exercises 1–5 and prepare questions for next class discussion.',
    deadline: '28 Mar 2025',
  },
  5: {
    title: 'Essay draft',
    description: 'Write the opening paragraph for short story analysis.',
    deadline: '30 Mar 2025',
  },
}

const MOCK_ASSESSMENT: Record<number, { title: string; description: string; deadline: string }> = {
  1: {
    title: 'Unit 4 — Reading comprehension',
    description: 'Based on chapters 3–5 material. Open-book format.',
    deadline: '2 Apr 2025',
  },
}

export function ViewLessonSheet() {
  const { t } = useTranslation()
  const { viewLesson, viewLessonOpen, closeViewLesson, editFromView, duplicateFromView } =
    useLessonSheet()

  if (!viewLesson) return null

  const journal = JOURNALS.find((j) => j.id === viewLesson.journalId)
  const homework = MOCK_HOMEWORK[viewLesson.id]
  const assessment = MOCK_ASSESSMENT[viewLesson.id]
  const slotLabel = LESSON_SLOT_LABELS[viewLesson.slot]

  return (
    <Sheet
      open={viewLessonOpen}
      onOpenChange={(open) => {
        if (!open) closeViewLesson()
      }}
    >
      <SheetContent className="flex w-full flex-col sm:max-w-4xl" side="right">
        <SheetHeader>
          <SheetTitle>{viewLesson.topic}</SheetTitle>
          <SheetDescription>
            {viewLesson.journalName} · {viewLesson.eventDateLabel}
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4 md:flex-row md:gap-0">
          {/* Left Panel — Metadata */}
          <div className="space-y-4 md:w-72 md:shrink-0 md:pr-6">
            <div
              className="rounded-lg p-4 text-white"
              style={{ backgroundColor: viewLesson.color }}
            >
              <p className="text-sm font-medium opacity-80">
                {t('ekoolTeacher.journal', 'Journal')}
              </p>
              <p className="text-lg font-semibold">{viewLesson.journalName}</p>
              {journal && (
                <p className="mt-1 text-sm opacity-80">
                  {t('ekoolTeacher.studentsCount', '{{count}} students', {
                    count: journal.students,
                  })}
                </p>
              )}
            </div>

            <Button variant="outline" className="w-full gap-1.5">
              <Mail className="size-4" />
              {t('ekoolTeacher.sendMessage', 'Send message')}
            </Button>

            <div className="space-y-3">
              <DetailRow
                icon={<CalendarDays className="text-muted-foreground size-4" />}
                label={t('ekoolTeacher.date', 'Date')}
                value={viewLesson.eventDateLabel}
              />
              <DetailRow
                icon={<Clock className="text-muted-foreground size-4" />}
                label={t('ekoolTeacher.lessonNumber', 'Lesson number')}
                value={`${viewLesson.slot}. ${slotLabel ?? viewLesson.time}`}
              />
              <DetailRow
                icon={<BookOpen className="text-muted-foreground size-4" />}
                label={t('ekoolTeacher.lessonType', 'Type')}
                value={viewLesson.lessonType}
              />
              <DetailRow
                icon={<User className="text-muted-foreground size-4" />}
                label={t('ekoolTeacher.teacher', 'Teacher')}
                value={viewLesson.teacher}
              />
              <DetailRow
                icon={<CalendarDays className="text-muted-foreground size-4" />}
                label={t('ekoolTeacher.created', 'Created')}
                value={viewLesson.created}
              />
            </div>

            {viewLesson.locked && (
              <Badge variant="secondary" className="w-fit">
                {t('ekoolTeacher.locked', 'Locked')}
              </Badge>
            )}
          </div>

          <Separator orientation="vertical" className="hidden md:block" />
          <Separator className="md:hidden" />

          {/* Right Panel — Content */}
          <div className="flex-1 space-y-4 md:pl-6">
            <h2 className="text-xl font-semibold">{viewLesson.topic}</h2>

            {viewLesson.descriptionHtml ? (
              <div
                className="text-muted-foreground prose prose-sm max-w-none text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: viewLesson.descriptionHtml }}
              />
            ) : (
              <p className="text-muted-foreground text-sm italic">
                {t('ekoolTeacher.noDescription', 'No description provided.')}
              </p>
            )}

            {homework && (
              <div className="space-y-2 rounded-lg border p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <FileText className="text-muted-foreground size-4" />
                  {t('ekoolTeacher.homework', 'Homework')}
                </h3>
                <p className="text-foreground text-sm font-medium">{homework.title}</p>
                <p className="text-muted-foreground text-sm">{homework.description}</p>
                <p className="text-muted-foreground text-xs">
                  {t('ekoolTeacher.deadline', 'Deadline')}: {homework.deadline}
                </p>
              </div>
            )}

            {assessment && (
              <div className="space-y-2 rounded-lg border p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <ClipboardCheck className="text-muted-foreground size-4" />
                  {t('ekoolTeacher.assessment', 'Assessment')}
                </h3>
                <p className="text-foreground text-sm font-medium">{assessment.title}</p>
                <p className="text-muted-foreground text-sm">{assessment.description}</p>
                <p className="text-muted-foreground text-xs">
                  {t('ekoolTeacher.deadline', 'Deadline')}: {assessment.deadline}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" className="gap-1.5" onClick={duplicateFromView}>
            <Copy className="size-4" />
            {t('ekoolTeacher.duplicate', 'Duplicate')}
          </Button>
          <Button className="gap-1.5" onClick={editFromView}>
            <Pencil className="size-4" />
            {t('ekoolTeacher.editLesson', 'Edit lesson')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="text-foreground text-sm">{value}</p>
      </div>
    </div>
  )
}
